import { useEffect, useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import Modal from './Modal';
import { Button } from './Button';
import { Input } from './Field';

/**
 * Confirmation for destructive actions (docs/00-design-blueprint.md §7.4).
 * `confirmText` turns it into a typed confirmation for irreversible operations.
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  detail,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  confirmText = null,
  icon: Icon = destructive ? Trash2 : AlertTriangle,
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTyped('');
      setBusy(false);
    }
  }, [open]);

  const ready = !confirmText || typed.trim().toUpperCase() === confirmText.toUpperCase();

  const handleConfirm = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={440}
      className={destructive ? 'modal--danger' : undefined}
      title={title}
      icon={
        <div className="modal__icon" aria-hidden="true">
          <Icon size={18} />
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={handleConfirm} loading={busy} disabled={!ready}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--sp-3)' }}>
        <p style={{ fontSize: 'var(--fs-cell)', lineHeight: 1.6 }}>{message}</p>
        {detail && <p className="meta">{detail}</p>}
        {confirmText && (
          <Input
            label={`Type ${confirmText} to confirm`}
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmText}
          />
        )}
      </div>
    </Modal>
  );
}
